import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import type * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Menu rozwijane.
 *
 * Przez Radix, bo poprawne menu to nie lista pod przyciskiem: to pułapka fokusu,
 * obsługa strzałek i `Home`/`End`, zamykanie klawiszem `Escape` i kliknięciem
 * poza obszarem, zwracanie fokusu na przycisk po zamknięciu oraz `aria-expanded`
 * na wyzwalaczu. Napisanie tego samemu kończy się menu, którego nie da się
 * obsłużyć klawiaturą — a to jedyna droga na telewizorze.
 *
 * Wejście animuje `styles.css` przez `data-slot` i `data-state`; klasy
 * `animate-in` z przykładów shadcn są tu nazwami bez reguły, bo wtyczki animacji
 * w projekcie nie ma.
 */

export const DropdownMenu = DropdownMenuPrimitive.Root;
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

export function DropdownMenuContent({
  className,
  sideOffset = 6,
  align = "end",
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        align={align}
        className={cn(
          "z-50 min-w-[12rem] overflow-hidden rounded-lg border border-border bg-popover p-1",
          "text-popover-foreground shadow-xl shadow-black/40 backdrop-blur-xl",
          className,
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}

export function DropdownMenuItem({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item>) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      className={cn(
        "focus-ring-own relative flex cursor-default items-center gap-2 rounded-md px-2 py-2 text-sm outline-none select-none",
        // Radix zaznacza pozycję pod kursorem I pod strzałkami tym samym
        // atrybutem, więc jedna reguła obsługuje mysz, klawiaturę i pilota.
        "data-[highlighted]:bg-secondary data-[highlighted]:text-foreground",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        "[&_svg]:size-4 [&_svg]:shrink-0",
        className,
      )}
      {...props}
    />
  );
}

export function DropdownMenuLabel({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label>) {
  return (
    <DropdownMenuPrimitive.Label
      data-slot="dropdown-menu-label"
      className={cn("px-2 py-1.5 text-xs font-medium text-muted-foreground", className)}
      {...props}
    />
  );
}

export function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn("-mx-1 my-1 h-px bg-border", className)}
      {...props}
    />
  );
}
